import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Check, Zap, Gift, MessageCircle, Shield, Phone, Clock, CreditCard, Globe, Calendar, Sparkles } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { MembershipCardForm } from '@/components/ui/membership-card-form';
import { useLanguage } from '@/contexts/LanguageContext';
import { VillatechTestimonialsSection } from '@/components/ui/villatech-testimonials';
import { ProductDiscountCalculator } from '@/components/ui/product-discount-calculator';
import { MembershipPlansSection } from '@/components/membership/MembershipPlansSection';

// Logos de membresías
import logoBronce from '@/assets/membresia-logos/logo-bronce-new.png';
import logoPlata from '@/assets/membresia-logos/logo-plata-new.png';
import logoOro from '@/assets/membresia-logos/logo-oro-new.png';
import cardBronce from '@/assets/membresia-logos/card-bronce.png';
import cardSilver from '@/assets/membresia-logos/card-silver.png';
import cardGold from '@/assets/membresia-logos/card-gold.png';
import cardLogoBronce from '@/assets/membresia-logos/card-logo-bronce.png';
import cardLogoPlata from '@/assets/membresia-logos/card-logo-plata.png';
import cardLogoOro from '@/assets/membresia-logos/card-logo-oro.png';

// Hook para el countdown
const useCountdown = (targetDate: Date) => {
  const calculateTimeLeft = () => {
    const difference = targetDate.getTime() - new Date().getTime();
    
    if (difference <= 0) {
      return { days: 0, hours: 0, minutes: 0, seconds: 0 };
    }
    
    return {
      days: Math.floor(difference / (1000 * 60 * 60 * 24)),
      hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
      minutes: Math.floor((difference / 1000 / 60) % 60),
      seconds: Math.floor((difference / 1000) % 60),
    };
  };

  const [timeLeft, setTimeLeft] = useState(calculateTimeLeft());

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  return timeLeft;
};

const MembresiaAnualBronce: React.FC = () => {
  const { language, setLanguage } = useLanguage();
  const isEn = language === 'en';
  
  // Fecha límite: 31 de enero 2026 a las 23:59:59
  const targetDate = new Date('2026-01-31T23:59:59');
  const timeLeft = useCountdown(targetDate);

  // State for the card form modals
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isPlataFormOpen, setIsPlataFormOpen] = useState(false);
  const [isOroFormOpen, setIsOroFormOpen] = useState(false);

  // Translations
  const t = {
    heroOffer: isEn ? 'Bronze Membership' : 'Membresía Bronce',
    heroTitle1: isEn ? 'Protect your phone' : 'Protegé tu celular',
    heroTitle2: isEn ? 'ALL YEAR' : 'TODO EL AÑO',
    heroTitle3: isEn ? 'with a single payment' : 'con un solo pago',
    heroSubtitle: isEn ? 'Receive protection every month and discounts on our entire store' : 'Recibí protección todos los meses y descuentos en toda nuestra tienda',
    countdownLabel: isEn ? '⏰ Offer ends in:' : '⏰ La oferta termina en:',
    days: isEn ? 'Days' : 'Días',
    hours: isEn ? 'Hours' : 'Horas',
    min: isEn ? 'Min' : 'Min',
    sec: isEn ? 'Sec' : 'Seg',
    ctaButton: isEn ? '👉 ACTIVATE NOW' : '👉 ACTIVAR AHORA',
    regularPrice: isEn ? 'Regular price' : 'Precio regular',
    launchPrice: isEn ? 'Launch price' : 'Precio de lanzamiento',
    youSave: isEn ? 'You save' : 'Ahorrás',
    twoMonthsFree: isEn ? '2 months FREE!' : '¡2 meses GRATIS!',
    whatYouGet: isEn ? 'What you get every month' : 'Lo que recibís cada mes',
    benefit1: isEn ? '1 Simple Tempered or Hydrogel HD / month' : '1 Temperado sencillo o Hidrogel HD / mes',
    benefit2: isEn ? '1 Space Case / month' : '1 Estuche Space / mes',
    benefit3: isEn ? '2 Cleanings / month' : '2 Limpiezas / mes',
    benefit4: isEn ? '24-hour Tempered warranty' : 'Garantía de Temperado 24 horas',
    benefit5: isEn ? '5-10% discount on products' : '5-10% de descuento en productos',
    benefit6: isEn ? '10% discount on repairs' : '10% de descuento en reparaciones',
    howItWorks: isEn ? 'How does it work?' : '¿Cómo funciona?',
    step1Title: isEn ? 'Make a single payment' : 'Hacés un solo pago',
    step1Desc: isEn ? '₡120,000 for 12 months of protection' : '₡120.000 por 12 meses de protección',
    step2Title: isEn ? 'Instant discount' : 'Descuento inmediato',
    step2Desc: isEn ? '5-10% OFF on your current purchase' : '5-10% OFF en tu compra actual',
    step3Title: isEn ? 'Monthly benefits' : 'Beneficios mensuales',
    step3Desc: isEn ? 'Pick up your glass and case every month' : 'Retirá tu vidrio y estuche cada mes',
    step4Title: isEn ? 'Coordinate via WhatsApp' : 'Coordinás por WhatsApp',
    step4Desc: isEn ? 'Easy redemption at any branch' : 'Canje fácil en cualquier sucursal',
    yourDiscounts: isEn ? 'Your discounts with the membership' : 'Tus descuentos con la membresía',
    activateMembership: isEn ? 'Activate Membership' : 'Activar Membresía',
    faqTitle: isEn ? 'Frequently asked questions' : 'Preguntas frecuentes',
    faq1Q: isEn ? 'How long does the membership last?' : '¿Cuánto dura la membresía?',
    faq1A: isEn ? 'The annual membership lasts 12 months from the activation date.' : 'La membresía anual tiene una duración de 12 meses desde el momento de activación.',
    faq2Q: isEn ? 'Can I upgrade to a higher plan later?' : '¿Puedo subir de plan después?',
    faq2A: isEn ? 'Yes, you can upgrade at any time by paying the difference.' : 'Sí, podés subir de plan en cualquier momento pagando la diferencia.',
    faq3Q: isEn ? 'How do I claim my monthly benefits?' : '¿Cómo reclamo mis beneficios mensuales?',
    faq3A: isEn ? 'You coordinate directly via WhatsApp or by visiting any branch.' : 'Coordinás directamente por WhatsApp o visitando cualquier sucursal.',
    faq4Q: isEn ? 'What if my tempered glass breaks?' : '¿Qué pasa si se me rompe el temperado?',
    faq4A: isEn ? 'With the 24-hour warranty, if it breaks within 24 hours of installation, we replace it free.' : 'Con la garantía de 24 horas, si se rompe dentro de las 24 horas de instalado, te lo reponemos gratis.',
    faq5Q: isEn ? 'Is there limited availability?' : '¿Hay disponibilidad limitada?',
    faq5A: isEn ? 'Yes. Only 50 annual memberships available at launch price until January 31.' : 'Sí. Solo hay 50 membresías anuales disponibles con precio de lanzamiento hasta el 31 de enero.',
    readyToSave: isEn ? 'Ready to ' : '¿Listo para ',
    allYear: isEn ? 'save' : 'ahorrar',
    ctaDescription: isEn ? 'Activate your membership now and start enjoying your benefits TODAY.' : 'Activá tu membresía ahora y empezá a disfrutar tus beneficios HOY MISMO.',
    promoValidUntil: isEn ? 'Promotion valid until January 31 or while supplies last.' : 'Promoción válida hasta el 31 de enero o hasta agotar existencias.',
    perYear: isEn ? '/year' : '/año',
    perMonth: isEn ? '/month' : '/mes',
    equivalent: isEn ? 'Equivalent to' : 'Equivale a',
    only50: isEn ? 'Only 50 available!' : '¡Solo 50 disponibles!',
  };

  const discountVisuals = [
    { category: 'Gaming', discount: '5%', image: 'https://images.unsplash.com/photo-1618366712010-f4ae9c647dcb?w=400&h=400&fit=crop', color: 'from-amber-700/60 to-amber-900/80' },
    { category: isEn ? 'Cases' : 'Estuches', discount: '5%', image: 'https://images.unsplash.com/photo-1601593346740-925612772716?w=400&h=400&fit=crop', color: 'from-amber-700/60 to-amber-900/80' },
    { category: isEn ? 'Glass' : 'Vidrios', discount: '5%', image: 'https://images.unsplash.com/photo-1585771724684-38269d6639fd?w=400&h=400&fit=crop', color: 'from-amber-700/60 to-amber-900/80' },
    { category: isEn ? 'Cables' : 'Cables', discount: '5%', image: 'https://images.unsplash.com/photo-1583394838336-acd977736f90?w=400&h=400&fit=crop', color: 'from-amber-700/60 to-amber-900/80' },
    { category: 'Audio', discount: '5%', image: 'https://images.unsplash.com/photo-1505740420928-5e560c06d30e?w=400&h=400&fit=crop', color: 'from-amber-700/60 to-amber-900/80' },
    { category: isEn ? 'Repairs' : 'Reparaciones', discount: '10%', image: 'https://images.unsplash.com/photo-1597673030062-0a0f1a801a31?w=400&h=400&fit=crop', color: 'from-amber-700/60 to-amber-900/80' },
  ];

  const formatPrice = (price: number) => `₡${price.toLocaleString('es-CR')}`;

  const planPrices = {
    regularPrice: 144000, // 12 * 12000
    launchPrice: 120000, // 10 meses (2 gratis)
    monthlyEquivalent: 10000,
  };

  const plan = {
    id: 'bronce',
    name: isEn ? 'Bronze' : 'Bronce',
    subtitle: isEn ? 'Protection' : 'Protección',
    regularPrice: formatPrice(planPrices.regularPrice),
    launchPrice: formatPrice(planPrices.launchPrice),
    logo: logoBronce,
    cardLogo: cardLogoBronce,
  };

  const planPlata = {
    id: 'plata',
    name: isEn ? 'Silver' : 'Plata',
    subtitle: isEn ? 'Premium Protection' : 'Protección Premium',
    regularPrice: '₡324.000',
    launchPrice: '₡270.000',
    logo: logoPlata,
    cardLogo: cardLogoPlata,
  };

  const planOro = {
    id: 'oro',
    name: isEn ? 'Gold' : 'Oro',
    subtitle: isEn ? 'VIP Protection' : 'Protección VIP',
    regularPrice: '₡540.000',
    launchPrice: '₡450.000',
    logo: logoOro,
    cardLogo: cardLogoOro,
  };

  const benefits = [
    { text: t.benefit1, icon: Shield, image: 'https://images.unsplash.com/photo-1585771724684-38269d6639fd?w=300&h=200&fit=crop' },
    { text: t.benefit2, icon: Phone, image: 'https://images.unsplash.com/photo-1601593346740-925612772716?w=300&h=200&fit=crop' },
    { text: t.benefit3, icon: Sparkles, image: 'https://images.unsplash.com/photo-1563986768609-322da13575f3?w=300&h=200&fit=crop' },
    { text: t.benefit4, icon: Clock, image: 'https://images.unsplash.com/photo-1592899677977-9c10ca588bbd?w=300&h=200&fit=crop' },
    { text: t.benefit5, icon: Gift, image: 'https://images.unsplash.com/photo-1505740420928-5e560c06d30e?w=300&h=200&fit=crop' },
    { text: t.benefit6, icon: Zap, image: 'https://images.unsplash.com/photo-1597673030062-0a0f1a801a31?w=300&h=200&fit=crop' },
  ];

  const steps = [
    { number: "1", title: t.step1Title, description: t.step1Desc, icon: "💳" },
    { number: "2", title: t.step2Title, description: t.step2Desc, icon: "💥" },
    { number: "3", title: t.step3Title, description: t.step3Desc, icon: "📦" },
    { number: "4", title: t.step4Title, description: t.step4Desc, icon: "📱" },
  ];

  const faqs = [
    { question: t.faq1Q, answer: t.faq1A },
    { question: t.faq2Q, answer: t.faq2A },
    { question: t.faq3Q, answer: t.faq3A },
    { question: t.faq4Q, answer: t.faq4A },
    { question: t.faq5Q, answer: t.faq5A },
  ];

  const handleFormSubmit = (formData: any, planData: any) => {
    const whatsappNumber = "50688888888";
    const message = encodeURIComponent(
      `¡Hola! Quiero activar mi membresía ${planData.name} de VillaTech.\n\n` +
      `📋 Mis datos:\n` +
      `👤 Nombre: ${formData.nombre}\n` +
      `📱 WhatsApp: ${formData.telefono}\n` +
      `📧 Email: ${formData.email}\n` +
      `🆔 Cédula: ${formData.cedula}\n` +
      `📲 Celular: ${formData.modeloCelular}`
    );
    window.open(`https://wa.me/${whatsappNumber}?text=${message}`, '_blank');
  };

  return (
    <div className="min-h-screen bg-black text-white overflow-x-hidden">
      {/* LANGUAGE TOGGLE REMOVED */}

      <section className="min-h-screen relative flex items-center justify-center overflow-hidden pt-16 pb-20">
        {/* Background effects */}
        <div className="absolute inset-0 bg-gradient-to-br from-amber-950/30 via-black to-black" />
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-amber-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-amber-700/10 rounded-full blur-3xl" />
        
        <div className="container mx-auto px-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: -30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-4xl mx-auto"
          >
            <Badge className="mb-6 bg-amber-500/20 text-amber-400 border border-amber-500/40 text-lg px-4 py-2">
              {t.heroOffer}
            </Badge>

            <h1 className="text-4xl md:text-6xl font-black leading-tight mb-6">
              {t.heroTitle1}
              <br />
              <span className="bg-gradient-to-r from-amber-400 to-amber-600 bg-clip-text text-transparent">
                {t.heroTitle2}
              </span>
              <br />
              {t.heroTitle3}
            </h1>

            <p className="text-xl text-zinc-300 mb-8 max-w-lg mx-auto">
              {t.heroSubtitle}
            </p>

            {/* Floating Card below subtitle */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="flex justify-center mb-10"
            >
              <div className="relative">
                <motion.div
                  animate={{ y: [0, -10, 0] }}
                  transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                  className="relative"
                >
                  <img 
                    src={cardBronce} 
                    alt="Membresía Bronce Anual"
                    className="w-full max-w-sm drop-shadow-[0_20px_50px_rgba(217,119,6,0.3)]"
                  />
                </motion.div>
                {/* Glow effect behind card */}
                <div className="absolute inset-0 bg-gradient-to-r from-amber-500/20 to-amber-700/20 blur-3xl -z-10 scale-110" />
              </div>
            </motion.div>

            {/* Price highlight */}
            <div className="bg-zinc-900/80 border border-amber-500/30 rounded-2xl p-6 mb-8 max-w-md mx-auto
              shadow-[inset_0_2px_4px_rgba(255,255,255,0.05),0_8px_32px_rgba(0,0,0,0.4)]">
              <div className="flex items-center justify-between mb-4">
                <span className="text-zinc-400">{t.regularPrice}:</span>
                <span className="text-zinc-500 line-through text-xl">{formatPrice(planPrices.regularPrice)}</span>
              </div>
              <div className="flex items-center justify-between mb-4">
                <span className="text-amber-400 font-bold">{t.launchPrice}:</span>
                <span className="text-4xl font-black text-white">{formatPrice(planPrices.launchPrice)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-zinc-400">{t.equivalent}:</span>
                <span className="text-zinc-300">{formatPrice(planPrices.monthlyEquivalent)}{t.perMonth}</span>
              </div>
            </div>

            <Button
              onClick={() => setIsFormOpen(true)}
              size="lg"
              className="bg-gradient-to-r from-amber-500 to-amber-700 hover:from-amber-600 hover:to-amber-800 text-black font-bold text-xl px-10 py-8 rounded-2xl shadow-[0_0_40px_rgba(217,119,6,0.4)] hover:shadow-[0_0_60px_rgba(217,119,6,0.6)] transition-all duration-300"
            >
              <CreditCard className="w-6 h-6 mr-3" />
              {t.ctaButton}
            </Button>
          </motion.div>
        </div>
      </section>

      {/* BENEFICIOS MENSUALES */}
      <section className="py-20 bg-gradient-to-b from-black to-zinc-900">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-5xl font-bold mb-4">
              <span className="text-amber-400">{t.whatYouGet}</span>
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-zinc-900 border border-amber-700/30 rounded-2xl overflow-hidden
                    shadow-[inset_0_2px_4px_rgba(255,255,255,0.05),0_8px_24px_rgba(0,0,0,0.4)]
                    hover:border-amber-500/50 transition-all duration-300 group"
                >
                  {/* Product Image */}
                  <div className="relative h-40 overflow-hidden">
                    <img 
                      src={benefit.image} 
                      alt={benefit.text}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-zinc-900 via-transparent to-transparent" />
                    <div className="absolute top-3 right-3 w-10 h-10 bg-amber-500/90 rounded-xl flex items-center justify-center">
                      <Icon className="w-5 h-5 text-black" />
                    </div>
                  </div>
                  <div className="p-5 text-center">
                    <p className="text-white font-medium text-lg">{benefit.text}</p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CÓMO FUNCIONA */}
      <section className="py-20 bg-gradient-to-b from-zinc-900 to-black">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-5xl font-bold mb-4">
              {t.howItWorks}
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-4 gap-6 max-w-5xl mx-auto">
            {steps.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="relative bg-zinc-900 border border-zinc-700 rounded-2xl p-6 text-center
                  shadow-[inset_0_2px_4px_rgba(255,255,255,0.05),0_8px_24px_rgba(0,0,0,0.4)]"
              >
                <div className="text-4xl mb-4">{step.icon}</div>
                <div className="absolute -top-3 -left-3 w-8 h-8 rounded-full bg-amber-600 text-white font-bold flex items-center justify-center text-sm">
                  {step.number}
                </div>
                <h3 className="text-lg font-bold text-white mb-2">{step.title}</h3>
                <p className="text-zinc-400 text-sm">{step.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* DESCUENTOS */}
      <section className="py-20 bg-gradient-to-b from-black to-zinc-900">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-5xl font-bold mb-4">
              🏷️ <span className="text-amber-400">{t.yourDiscounts}</span>
            </h2>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6 max-w-6xl mx-auto">
            {discountVisuals.map((discount, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, scale: 0.8, y: 20 }}
                whileInView={{ opacity: 1, scale: 1, y: 0 }}
                whileHover={{ scale: 1.05, y: -8 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08, type: "spring", stiffness: 400, damping: 15 }}
                className="relative group cursor-pointer"
              >
                <div className="relative rounded-2xl overflow-hidden aspect-square 
                  shadow-[0_8px_30px_rgba(0,0,0,0.4)] 
                  border-2 border-amber-500/30
                  group-hover:shadow-[0_15px_40px_rgba(217,119,6,0.4)]
                  group-hover:border-amber-500/60
                  transition-all duration-300"
                >
                  <img 
                    src={discount.image} 
                    alt={discount.category}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className={`absolute inset-0 bg-gradient-to-t ${discount.color}`} />
                  <div className="absolute inset-0 flex flex-col items-center justify-center p-3">
                    <span className="text-white font-black text-3xl md:text-4xl drop-shadow-[0_4px_8px_rgba(0,0,0,0.9)]">
                      {discount.discount}
                    </span>
                    <span className="text-white text-xs md:text-sm font-bold text-center leading-tight drop-shadow-[0_2px_4px_rgba(0,0,0,0.9)] mt-2 bg-black/40 px-3 py-1 rounded-full">
                      {discount.category}
                    </span>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIOS */}
      <VillatechTestimonialsSection />

      {/* FAQs */}
      <section className="py-20 bg-gradient-to-b from-zinc-900 to-black">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-5xl font-bold mb-4">
              ❓ <span className="text-orange-400">{t.faqTitle}</span>
            </h2>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-3xl mx-auto"
          >
            <Accordion type="single" collapsible className="space-y-4">
              {faqs.map((faq, index) => (
                <AccordionItem
                  key={index}
                  value={`faq-${index}`}
                  className="bg-zinc-900 border border-zinc-700 rounded-2xl px-6 overflow-hidden"
                >
                  <AccordionTrigger className="text-left text-white hover:text-amber-400 py-5">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-zinc-400 pb-5">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </motion.div>
        </div>
      </section>

      {/* CTA FINAL */}
      <section className="py-20 bg-gradient-to-b from-black to-zinc-900 relative">
        <div className="absolute inset-0 bg-gradient-to-r from-amber-500/5 via-transparent to-amber-500/5" />
        
        <div className="container mx-auto px-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-3xl mx-auto text-center"
          >
            <Badge className="mb-6 bg-red-500/20 text-red-400 border border-red-500/40 animate-pulse">
              <Clock className="w-4 h-4 mr-2" />
              🔥 {t.only50}
            </Badge>

            <h2 className="text-2xl sm:text-3xl md:text-5xl font-bold mb-6 leading-tight">
              {t.readyToSave}<span className="text-amber-400">{t.allYear}</span>?
            </h2>

            <p className="text-xl text-zinc-300 mb-4">
              {t.ctaDescription}
            </p>

            {/* Price reminder */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 bg-zinc-900/80 border border-amber-500/30 rounded-2xl px-6 sm:px-8 py-4 mb-8">
              <span className="text-zinc-500 line-through text-lg sm:text-xl">{formatPrice(planPrices.regularPrice)}</span>
              <span className="text-3xl sm:text-4xl font-black text-amber-400">{formatPrice(planPrices.launchPrice)}</span>
            </div>

            <div className="block">
              <Button
                onClick={() => setIsFormOpen(true)}
                size="lg"
                className="bg-gradient-to-r from-amber-500 to-amber-700 hover:from-amber-600 hover:to-amber-800 text-black font-bold text-xl px-12 py-8 rounded-2xl shadow-[0_0_40px_rgba(217,119,6,0.4)] hover:shadow-[0_0_60px_rgba(217,119,6,0.6)] transition-all duration-300"
              >
                <CreditCard className="w-6 h-6 mr-3" />
                {t.activateMembership}
              </Button>
            </div>

            <p className="text-zinc-500 mt-6 text-sm">
              {t.promoValidUntil}
            </p>
          </motion.div>
        </div>
      </section>

      {/* UPSELL PLATA SECTION */}
      <section className="py-20 bg-gradient-to-b from-zinc-900 to-slate-900 relative overflow-hidden">
        {/* Background effects */}
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-slate-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-slate-400/10 rounded-full blur-3xl" />
        
        <div className="container mx-auto px-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <Badge className="mb-4 bg-slate-500/20 text-slate-300 border border-slate-500/40">
              <Sparkles className="w-4 h-4 mr-2" />
              Upgrade
            </Badge>
            <h2 className="text-3xl md:text-5xl font-bold mb-4">
              ¿Querés <span className="text-slate-300">más descuentos</span>?
            </h2>
            <p className="text-zinc-400 text-lg max-w-2xl mx-auto">
              Subí a Plata y duplicá tus beneficios mensuales
            </p>
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-12 items-center max-w-5xl mx-auto">
            {/* Plata Card */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="flex justify-center"
            >
              <div className="relative">
                <motion.div
                  animate={{ y: [0, -10, 0] }}
                  transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                  className="relative"
                >
                  <img 
                    src={cardSilver} 
                    alt="Membresía Plata"
                    className="w-full max-w-sm drop-shadow-[0_20px_50px_rgba(148,163,184,0.3)]"
                  />
                </motion.div>
                <div className="absolute inset-0 bg-gradient-to-r from-slate-500/20 to-slate-400/20 blur-3xl -z-10 scale-110" />
              </div>
            </motion.div>

            {/* Plata Benefits */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="bg-zinc-900/80 border border-slate-500/30 rounded-3xl p-8 shadow-[inset_0_2px_4px_rgba(255,255,255,0.05),0_8px_32px_rgba(0,0,0,0.4)]">
                <h3 className="text-2xl font-bold text-white mb-6">Membresía Plata</h3>
                
                <div className="space-y-4 mb-8">
                  <div className="flex items-center justify-between gap-3 bg-slate-500/10 rounded-xl p-3">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-slate-500/20 rounded-lg flex items-center justify-center">
                        <Shield className="w-4 h-4 text-slate-300" />
                      </div>
                      <span className="text-white">2 vidrios temperados por mes</span>
                    </div>
                    <Badge className="bg-green-500/20 text-green-400 border-green-500/40 text-xs">+1 vs Bronce</Badge>
                  </div>
                  <div className="flex items-center justify-between gap-3 bg-slate-500/10 rounded-xl p-3">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-slate-500/20 rounded-lg flex items-center justify-center">
                        <Phone className="w-4 h-4 text-slate-300" />
                      </div>
                      <span className="text-white">2 estuches Space por mes</span>
                    </div>
                    <Badge className="bg-green-500/20 text-green-400 border-green-500/40 text-xs">+1 vs Bronce</Badge>
                  </div>
                  <div className="flex items-center justify-between gap-3 bg-slate-500/10 rounded-xl p-3">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-slate-500/20 rounded-lg flex items-center justify-center">
                        <Gift className="w-4 h-4 text-slate-300" />
                      </div>
                      <span className="text-white font-bold">10-15% en productos</span>
                    </div>
                    <Badge className="bg-green-500/20 text-green-400 border-green-500/40 text-xs">+5-10% vs Bronce</Badge>
                  </div>
                  <div className="flex items-center justify-between gap-3 bg-slate-500/10 rounded-xl p-3">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-slate-500/20 rounded-lg flex items-center justify-center">
                        <Zap className="w-4 h-4 text-slate-300" />
                      </div>
                      <span className="text-white font-bold">15% en reparaciones</span>
                    </div>
                    <Badge className="bg-green-500/20 text-green-400 border-green-500/40 text-xs">+5% vs Bronce</Badge>
                  </div>
                </div>

                {/* Pricing comparison */}
                <div className="bg-zinc-800/50 rounded-2xl p-4 mb-6 space-y-3">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-zinc-400">Plata anual:</span>
                    <span className="text-white font-bold">₡270.000/año</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-zinc-400">Bronce anual:</span>
                    <span className="text-zinc-500">₡120.000/año</span>
                  </div>
                  <div className="flex items-center justify-between pt-3 border-t border-zinc-700">
                    <span className="text-slate-300 font-medium">Diferencia:</span>
                    <span className="text-2xl font-black text-slate-300">+₡150.000</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-zinc-400">Equivale a:</span>
                    <span className="text-slate-400">₡12.500/mes extra</span>
                  </div>
                </div>

                <Button
                  onClick={() => setIsPlataFormOpen(true)}
                  size="lg"
                  className="w-full bg-gradient-to-r from-slate-500 to-slate-600 hover:from-slate-600 hover:to-slate-700 text-white font-bold text-lg py-6 rounded-2xl shadow-[0_0_30px_rgba(148,163,184,0.3)]"
                >
                  <CreditCard className="w-5 h-5 mr-2" />
                  Activar Membresía Plata
                </Button>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* UPSELL ORO SECTION */}
      <section className="py-20 bg-gradient-to-b from-zinc-900 to-black relative overflow-hidden">
        {/* Background effects - mantener glow dorado solo para la tarjeta */}
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-blue-400/10 rounded-full blur-3xl" />
        
        <div className="container mx-auto px-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <Badge className="mb-4 bg-yellow-500/20 text-yellow-400 border border-yellow-500/40">
              <Sparkles className="w-4 h-4 mr-2" />
              VIP Premium
            </Badge>
            <h2 className="text-3xl md:text-5xl font-bold mb-4">
              ¿Querés el <span className="bg-gradient-to-r from-yellow-400 to-amber-500 bg-clip-text text-transparent">máximo ahorro</span>?
            </h2>
            <p className="text-zinc-400 text-lg max-w-2xl mx-auto">
              La membresía Oro te da los mejores descuentos y beneficios exclusivos VIP
            </p>
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-12 items-center max-w-5xl mx-auto">
            {/* Oro Card */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="flex justify-center"
            >
              <div className="relative">
                <motion.div
                  animate={{ y: [0, -10, 0] }}
                  transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                  className="relative"
                >
                  <img 
                    src={cardGold} 
                    alt="Membresía Oro"
                    className="w-full max-w-sm drop-shadow-[0_20px_50px_rgba(234,179,8,0.4)]"
                  />
                </motion.div>
                <div className="absolute inset-0 bg-gradient-to-r from-yellow-500/20 to-amber-400/20 blur-3xl -z-10 scale-110" />
              </div>
            </motion.div>

            {/* Oro Benefits */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="bg-zinc-900/80 border border-yellow-500/30 rounded-3xl p-8 shadow-[inset_0_2px_4px_rgba(255,255,255,0.05),0_8px_32px_rgba(0,0,0,0.4)]">
                <h3 className="text-2xl font-bold text-white mb-6">Membresía Oro</h3>
                
                <div className="space-y-4 mb-8">
                  <div className="flex items-center justify-between gap-3 bg-yellow-500/10 rounded-xl p-3">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-yellow-500/20 rounded-lg flex items-center justify-center">
                        <Shield className="w-4 h-4 text-yellow-400" />
                      </div>
                      <span className="text-white">2 hydrogel premium por mes</span>
                    </div>
                    <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/40 text-xs">Premium</Badge>
                  </div>
                  <div className="flex items-center justify-between gap-3 bg-yellow-500/10 rounded-xl p-3">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-yellow-500/20 rounded-lg flex items-center justify-center">
                        <Phone className="w-4 h-4 text-yellow-400" />
                      </div>
                      <span className="text-white">1 estuche premium por mes</span>
                    </div>
                    <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/40 text-xs">Premium</Badge>
                  </div>
                  <div className="flex items-center justify-between gap-3 bg-yellow-500/10 rounded-xl p-3">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-yellow-500/20 rounded-lg flex items-center justify-center">
                        <Sparkles className="w-4 h-4 text-yellow-400" />
                      </div>
                      <span className="text-white">3 limpiezas por mes</span>
                    </div>
                    <Badge className="bg-green-500/20 text-green-400 border-green-500/40 text-xs">+1 vs Plata</Badge>
                  </div>
                  <div className="flex items-center justify-between gap-3 bg-yellow-500/10 rounded-xl p-3">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-yellow-500/20 rounded-lg flex items-center justify-center">
                        <Gift className="w-4 h-4 text-yellow-400" />
                      </div>
                      <span className="text-white font-bold">15-20% en productos</span>
                    </div>
                    <Badge className="bg-green-500/20 text-green-400 border-green-500/40 text-xs">+10% vs Bronce</Badge>
                  </div>
                  <div className="flex items-center justify-between gap-3 bg-yellow-500/10 rounded-xl p-3">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-yellow-500/20 rounded-lg flex items-center justify-center">
                        <Zap className="w-4 h-4 text-yellow-400" />
                      </div>
                      <span className="text-white font-bold">20% en reparaciones</span>
                    </div>
                    <Badge className="bg-green-500/20 text-green-400 border-green-500/40 text-xs">+10% vs Bronce</Badge>
                  </div>
                  <div className="flex items-center justify-between gap-3 bg-yellow-500/10 rounded-xl p-3">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-yellow-500/20 rounded-lg flex items-center justify-center">
                        <MessageCircle className="w-4 h-4 text-yellow-400" />
                      </div>
                      <span className="text-white">Atención VIP directa</span>
                    </div>
                    <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/40 text-xs">Exclusivo</Badge>
                  </div>
                </div>

                {/* Pricing comparison */}
                <div className="bg-zinc-800/50 rounded-2xl p-4 mb-6 space-y-3">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-zinc-400">Oro anual:</span>
                    <span className="text-white font-bold">₡450.000/año</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-zinc-400">Bronce anual:</span>
                    <span className="text-zinc-500">₡120.000/año</span>
                  </div>
                  <div className="flex items-center justify-between pt-3 border-t border-zinc-700">
                    <span className="text-yellow-400 font-medium">Diferencia:</span>
                    <span className="text-2xl font-black bg-gradient-to-r from-yellow-400 to-amber-500 bg-clip-text text-transparent">+₡330.000</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-zinc-400">Equivale a:</span>
                    <span className="text-yellow-400/80">₡27.500/mes extra</span>
                  </div>
                </div>

                <Button
                  onClick={() => setIsOroFormOpen(true)}
                  size="lg"
                  className="w-full bg-gradient-to-r from-yellow-500 to-amber-600 hover:from-yellow-600 hover:to-amber-700 text-black font-bold text-lg py-6 rounded-2xl shadow-[0_0_30px_rgba(234,179,8,0.4)]"
                >
                  <CreditCard className="w-5 h-5 mr-2" />
                  Activar Membresía Oro
                </Button>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* SECCIÓN DE PLANES DE MEMBRESÍA */}
      <MembershipPlansSection />

      {/* Product Discount Calculator */}
      <ProductDiscountCalculator />

      {/* Card Form Modal - Bronce */}
      <MembershipCardForm
        plan={plan}
        isOpen={isFormOpen}
        onClose={() => setIsFormOpen(false)}
        onSubmit={handleFormSubmit}
      />

      {/* Card Form Modal - Plata */}
      <MembershipCardForm
        plan={planPlata}
        isOpen={isPlataFormOpen}
        onClose={() => setIsPlataFormOpen(false)}
        onSubmit={handleFormSubmit}
      />

      {/* Card Form Modal - Oro */}
      <MembershipCardForm
        plan={planOro}
        isOpen={isOroFormOpen}
        onClose={() => setIsOroFormOpen(false)}
        onSubmit={handleFormSubmit}
      />
    </div>
  );
};

export default MembresiaAnualBronce;
